import { useQuery } from '@tanstack/vue-query'
import { useSessionStorage } from '@vueuse/core'
import { useApi } from '@/composables/useApi'

type TutorialResource = {
  id: string
  uuid: string
  name: string
  slug: string
  published_at: boolean
  metas: {
    song_original_title: string | null
    song_album: string | null
    song_author: string | null
    song_performer: string | null
    song_published_at: string | null
    song_license: string | null
    song_tempo: number | null
    song_time_signature: string | null
    tutorial_key: string | null
    tutorial_time: string | null
  }
  labels: Record<string, string>
  image: {
    id: number
    name: string
    type: string
    url: string
    thumbnail: string
    size: string
    created_at: string
    updated_at: string
  }
}

type TutorialsPublicResponse = {
  random: TutorialResource[]
  latest: TutorialResource[]
  selected: TutorialResource[]
}

function fetchTutorials() {
  const { getApiUrl } = useApi()

  const cache = useSessionStorage<TutorialsPublicResponse>('tutorials-public', {
    random: [],
    latest: [],
    selected: []
  })

  const query = useQuery<TutorialsPublicResponse>({
    queryKey: ['tutorials-public'],
    queryFn: async() => {
      const response = await fetch(getApiUrl('api/material/tutorials/public'))

      if (!response.ok) {
        return {
          random: [],
          latest: [],
          selected: []
        } satisfies TutorialsPublicResponse
      }
      const data = await response.json() as TutorialsPublicResponse
      cache.value = data
      return data
    },
    initialData: () => cache.value,
    placeholderData: () => cache.value,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  })

  query.refetch()

  return query
}

export function useTutorials() {
  return {
    fetchTutorials,
  }
}
